<template>
  <div
    class="flex items-center gap-2"
    :class="{'cursor-pointer': !!card.membership}"
    @click="card.membership && $emit('manage', card)"
  >
    <img
      v-if="card.file"
      :src="card.file.url"
      :alt="card.name"
      class="w-[52px] rounded-md object-cover shadow-sm shrink-0"
      :style="{aspectRatio: LOYALTY_CARD_ASPECT_RATIO}"
    >
    <div
      v-else
      class="relative w-[52px] rounded-md shrink-0 flex items-center justify-center text-white font-bold text-tiny overflow-hidden"
      :class="{'bg-white shadow-sm': showLogo}"
      :style="{
        aspectRatio: LOYALTY_CARD_ASPECT_RATIO,
        background: showLogo ? undefined : `linear-gradient(135deg, ${analysis.gradient[0]}, ${analysis.gradient[1]})`
      }"
    >
      <span v-if="!showLogo">{{ analysis.initials }}</span>
      <img
        v-if="hasLogo"
        :src="analysis.logoUrl ?? ''"
        :alt="analysis.shortName"
        class="absolute inset-0 w-full h-full object-contain p-1"
        :class="{'invisible': !showLogo}"
        @load="onLoad"
        @error="onError"
      >
    </div>

    <template v-if="!card.membership">
      <span class="text-sm font-semibold flex-1 min-w-0 truncate">{{ analysis.shortName }}</span>
      <span class="text-xs text-gray-500 shrink-0">Nouvelle carte</span>
      <button
        class="shrink-0 bg-primary text-white text-sm font-semibold rounded-full px-3.5 py-1.5"
        @click.stop="$emit('activate', card)"
      >
        Activer
      </button>
    </template>

    <template v-else>
      <div class="flex-1 min-w-0">
        <div class="text-sm font-semibold truncate">
          {{ analysis.shortName }}
        </div>
      </div>
      <div class="text-right shrink-0">
        <div class="text-sm font-bold text-primary">
          {{ card.membership.formatted?.point_balance ?? card.membership.point_balance }}
        </div>
        <div v-if="discountInEuro" class="text-xs text-gray-500">
          {{ discountInEuro }} € de remise
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import {computed} from "vue";
import {LOYALTY_CARD_ASPECT_RATIO} from "@/loyalty-card/interfaces/hds/LoyaltyCard";
import useSponsorLogo from "@/notification/composables/useSponsorLogo";
import {DetectionLoyaltyCard} from "@/notification/interfaces";
import {DetectionCardAnalysis} from "@/notification/components/notification-detection/loyalty-cards/detectionCardAnalysis";

const props = defineProps<{
    card: DetectionLoyaltyCard;
    /** Computed once by the parent. */
    analysis: DetectionCardAnalysis;
}>();

defineEmits<{
    activate: [card: DetectionLoyaltyCard];
    manage: [card: DetectionLoyaltyCard];
}>();

const {hasLogo, showLogo, onLoad, onError} = useSponsorLogo(computed(() => props.analysis.logoUrl));

/** Euro value of the point balance (same rule as LoyaltyCardMembershipPointBalance). */
const discountInEuro = computed(() => {
    if (!props.card.membership || !props.card.point?.rate) {
        return null;
    }

    return (props.card.membership.point_balance * props.card.point.rate).toFixed(2);
});
</script>
